import moment from "moment";

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Action, action, createStore, createTypedHooks, State } from "easy-peasy";

import { getLanguageCode } from "./imports/Utils";
import { getRegionLocale } from "./utils/regionUtils";

import { GasoilPrice } from "__generated__/graphql";
import { StatusType } from "__generated__/enums";
import { DefaultTableSettingsFragment } from "__generated__/view/defaultTableSettings/graphql/defaultTableSettings.fragments.generated";
import { StatusFragmentFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import { CommentFragment, CurrencyFragment } from "__generated__/graphql/fragments.generated";

import { Language, BOSSettings, BOSUserDetails } from "types/commons";

export type SessionState = {
    version: string;
    locale: Language;
    regionLocale: string;
	language:  {
		language: string,
		values: { [key: string]: string }
	} | null;
    user: BOSUserDetails | null;
    settings: BOSSettings | null;
    publicSettings: BOSSettings | null;
    redirectUrl: string;
    currencies: CurrencyFragment[];
    statuses: Record<StatusType, StatusFragmentFragment[]> | null;
    tableSettings: DefaultTableSettingsFragment[] | null;
    gasoilprice: GasoilPrice | null;
    readComments: { date: number, comments: Pick<CommentFragment, "id" | "created">[] };
};

export type SessionModel = SessionState & {
	setUser: Action<SessionModel, {
		user: BOSUserDetails
	}>;
	updateUser: Action<SessionModel, BOSUserDetails>,
	setAppSettings: Action<SessionModel, Partial<SessionModel>>,
	setPlugins: Action<SessionModel, BOSSettings>,
	setVersion: Action<SessionModel, string>,
	setTableSettings: Action<SessionModel, DefaultTableSettingsFragment[]>;
	setInitialSettings: Action<SessionModel, {
		language: {
			language: string,
			values: { [key: string]: string }
		} | null,
		version: string,
		publicSettings: BOSSettings
	}>,
	logout: Action<SessionModel>,
	addReadComments: Action<SessionModel, Pick<CommentFragment, "id" | "created">[]>
	agreeToTermsOfService: Action<SessionModel>,
}

export type StoreModel = {
	session: SessionModel
	setRedirectUrl: Action<StoreModel, string>;
}

export type Session = State<SessionModel>;

const initialSessionState: SessionState = {
	version: "",
	locale: "EN",
	regionLocale: "en-NL",
	language: null,
	user: null,
	settings: null,
	publicSettings: null,
	redirectUrl: "",
	currencies: [],
	statuses: null,
	tableSettings: null,
	gasoilprice: null,
	readComments: {
		date: Date.now(),
		comments: [],
	},
};

const ezStore = createStore<StoreModel>({
	session: {
		...initialSessionState,

		setUser: action((state, payload) => {
			if (payload.user.language) {
				moment.locale(payload.user.language);
			}

			const regionLocale = getRegionLocale(payload.user.region, payload.user.language);
			localStorage.setItem("bosRegionLocale", regionLocale);

			return {
				...state,
				user: payload.user,
				locale: getLanguageCode(payload.user.language),
				regionLocale,
			};
		}),
		updateUser: action((state, payload) => {
			return {
				...state,
				user: payload,
			};
		}),
		setAppSettings: action((state, payload) => {
			return {
				...state,
				...payload,
			};
		}),
		setPlugins: action((state, payload) => {
			return {
				...state,
				settings: { ...(state.settings ?? {}), ...payload },
			};
		}),
		setVersion: action((state, payload) => {
			return {
				...state,
				version: payload,
			};
		}),
		setTableSettings: action((state, payload) => {
			return {
				...state,
				tableSettings: payload,
			};
		}),
		setInitialSettings: action((state, payload) => {
			return {
				...state,
				publicSettings: payload.publicSettings,
				language: payload.language,
				locale: initialSessionState.locale,
				version: payload.version,
			};
		}),
		logout: action(state => {
			return {
				...initialSessionState,
				version: state.version,
				redirectUrl: state.redirectUrl,
			};
		}),
		addReadComments: action((state, payload) => {
			return {
				...state,
				readComments: {
					comments: [ ...state.readComments.comments, ...payload ],
					date: new Date().getTime(),
				},
			};
		}),
		agreeToTermsOfService: action((state) => {
			if (!state.user) {
				return state;
			}

			return {
				...state,
				user: {
					...state.user,
					properties: {
						...(state.user.properties ?? {}),
						validated: true,
					},
				},
			};
		}),
	},
	setRedirectUrl: action((state, payload) => {
		return {
			...state,
			session: {
				...state.session,
				redirectUrl: payload,
			},
		};
	}),
}, {
	disableImmer: true,
});

const typedHooks = createTypedHooks<StoreModel>();
export const { useStoreActions, useStoreDispatch, useStoreState } = typedHooks;
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
export { useStoreRehydrated } from "easy-peasy";
export default ezStore;
